import React, { useMemo } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { colorForSite } from "../../../utils/featureDistribution";

// Grouped histogram for a numeric feature.
// SVG-based (no charting library dep). Each `bin_edges[i]..bin_edges[i+1]`
// becomes one bin slot; within the slot, one thin bar per site, side-by-side.
// Site colors come from utils/featureDistribution.SITE_COLORS.
//
// Inputs:
//   feature.bin_edges: number[N+1]  (shared by all sites)
//   feature.distributions: [
//     { site_id, display_name, counts: number[N] (or "<5" strings),
//       n_valid, n_missing, stats: {...} }
//   ]
//
// Suppressed-cell handling: when `suppress_small_cells=true` is requested,
// backend returns the string "<5" instead of an int. We render those as
// a half-height neutral-grey bar with a tooltip explaining suppression.
//
// Width/height controlled by parent; chart area auto-scales.
const NumericHistogram = ({ feature, height = 140 }) => {
  const data = useMemo(() => {
    const edges = Array.isArray(feature?.bin_edges) ? feature.bin_edges : [];
    const numBins = Math.max(0, edges.length - 1);
    const dists = Array.isArray(feature?.distributions) ? feature.distributions : [];
    // Find the max int across all (site, bin) for y-axis scaling. Strings
    // (suppressed) don't influence the scale.
    let maxCount = 0;
    for (const d of dists) {
      const counts = Array.isArray(d.counts) ? d.counts : [];
      for (const c of counts) {
        if (typeof c === 'number' && Number.isFinite(c) && c > maxCount) maxCount = c;
      }
    }
    return { numBins, edges, dists, maxCount };
  }, [feature]);

  const { numBins, edges, dists, maxCount } = data;
  if (numBins === 0 || dists.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--fx-text-muted)', fontSize: '0.75rem' }}>
        No bins to plot
      </Box>
    );
  }

  // Layout constants
  const PAD_L = 26;   // left padding for y-axis labels
  const PAD_R = 6;
  const PAD_T = 6;
  const PAD_B = 22;   // bottom padding for x-axis labels
  const VIEW_W = 280; // logical viewBox width; SVG scales responsively
  const VIEW_H = height;
  const plotW = VIEW_W - PAD_L - PAD_R;
  const plotH = VIEW_H - PAD_T - PAD_B;

  const binSlotW = plotW / numBins;
  const groupGap = Math.max(0.5, binSlotW * 0.12);
  const barAreaW = binSlotW - groupGap;
  const barW = Math.max(0.5, barAreaW / Math.max(1, dists.length));

  // Y-axis: 4 grid lines, plain integer ticks
  const yMax = Math.max(1, maxCount);
  const yTicks = 4;
  const yTickVals = Array.from({ length: yTicks + 1 }, (_, i) =>
    Math.round((yMax * i) / yTicks)
  );

  // X-axis: show at most ~5 evenly-spaced bin-edge labels
  const xLabelStep = Math.max(1, Math.floor(numBins / 5));

  return (
    <Box sx={{ width: '100%', height }}>
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: '100%', display: 'block' }}
      >
        {/* Y-axis gridlines + tick labels */}
        {yTickVals.map((v, i) => {
          const y = PAD_T + plotH - (plotH * (v / yMax));
          return (
            <g key={`y-${i}`}>
              <line
                x1={PAD_L}
                x2={PAD_L + plotW}
                y1={y}
                y2={y}
                stroke="var(--fx-border)"
                strokeWidth={0.6}
                strokeDasharray="2,2"
              />
              <text
                x={PAD_L - 3}
                y={y + 3}
                textAnchor="end"
                fontSize="7"
                fill="var(--fx-text-muted)"
              >
                {v}
              </text>
            </g>
          );
        })}

        {/* Bars: bin slot × site */}
        {Array.from({ length: numBins }).map((_, binIdx) => {
          const slotX = PAD_L + binIdx * binSlotW + groupGap / 2;
          return (
            <g key={`bin-${binIdx}`}>
              {dists.map((d, siteIdx) => {
                const raw = d.counts?.[binIdx];
                const isSuppressed = typeof raw === 'string';
                const value = typeof raw === 'number' ? raw : 0;
                const h = isSuppressed
                  ? Math.max(2, plotH * 0.08)            // squat neutral bar
                  : (plotH * value) / yMax;
                const x = slotX + siteIdx * barW;
                const y = PAD_T + plotH - h;
                const color = isSuppressed ? 'var(--fx-border-strong)' : colorForSite(d.site_id, siteIdx);
                const title = isSuppressed
                  ? `${d.display_name || d.site_id} · bin ${binIdx + 1}: suppressed (<5)`
                  : `${d.display_name || d.site_id} · bin ${binIdx + 1}: ${value}`;
                return (
                  <Tooltip key={`bar-${binIdx}-${siteIdx}`} title={title} placement="top">
                    <rect
                      x={x}
                      y={y}
                      width={Math.max(0.5, barW - 0.3)}
                      height={Math.max(0, h)}
                      fill={color}
                      opacity={isSuppressed ? 0.65 : 0.92}
                      rx={0.5}
                    />
                  </Tooltip>
                );
              })}
            </g>
          );
        })}

        {/* X-axis edge labels */}
        {edges.map((e, i) => {
          if (i % xLabelStep !== 0 && i !== edges.length - 1) return null;
          const x = PAD_L + (i * plotW) / numBins;
          const labelTxt =
            Math.abs(e) >= 100 ? e.toFixed(0)
              : Math.abs(e) >= 10 ? e.toFixed(1)
              : e.toFixed(2);
          return (
            <text
              key={`xl-${i}`}
              x={x}
              y={VIEW_H - 8}
              textAnchor="middle"
              fontSize="7"
              fill="var(--fx-text-secondary)"
            >
              {labelTxt}
            </text>
          );
        })}

        {/* X-axis baseline */}
        <line
          x1={PAD_L}
          x2={PAD_L + plotW}
          y1={PAD_T + plotH}
          y2={PAD_T + plotH}
          stroke="var(--fx-border-strong)"
          strokeWidth={0.6}
        />
      </svg>

      {/* Per-site stats footer (compact). Only render when stats present. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
        {dists.map((d) => {
          const stats = d.stats;
          if (!stats || typeof stats !== 'object') return null;
          const fmt = (n) =>
            typeof n === 'number' && Number.isFinite(n) ? n.toFixed(1) : 'n/a';
          return (
            <Tooltip
              key={`stats-${d.site_id}`}
              title={`min=${fmt(stats.min)} · q1=${fmt(stats.q1)} · median=${fmt(stats.median)} · q3=${fmt(stats.q3)} · max=${fmt(stats.max)} · μ=${fmt(stats.mean)} · σ=${fmt(stats.std)}`}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.6rem',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  color: 'var(--fx-text-secondary)',
                  bgcolor: 'var(--fx-subtle)',
                  px: 0.5,
                  py: 0.1,
                  borderRadius: 0.5,
                  border: `1px solid ${colorForSite(d.site_id)}33`,
                }}
              >
                μ={fmt(stats.mean)}
              </Typography>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};

export default NumericHistogram;
